import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

const PrestamoFlipping = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.toLowerCase(); // "es", "en", etc.

  const navigate = useNavigate();
  let metaTitle = '';
  let metaDescription = '';
  let metaKeywords = '';
  let ogTitle = '';
  let ogDescription = '';
  let faqJsonLd = '';

  // Lógica SEO según idioma
  if (currentLang === 'es') {
    metaTitle = 'Préstamo Flipping - Compra, Reforma y Venta Rápida | Finanbid';
    metaDescription =
      'Financiación ágil para comprar, reformar y vender inmuebles a corto plazo. Fondos especializados pujan para ofrecerte el mejor préstamo flipping.';
    metaKeywords =
      'préstamo flipping, fix and flip, inversión inmobiliaria, capital privado, compra-venta rápida, reforma, short-term loan';
    ogTitle = metaTitle;
    ogDescription = metaDescription;

    faqJsonLd = `
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "¿En qué consiste el flipping inmobiliario?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Es la compra de un inmueble a buen precio, su reforma y posterior venta a corto plazo, generando ganancia con la revalorización."
            }
          },
          {
            "@type": "Question",
            "name": "¿Por qué solicitar un Préstamo Flipping con Finanbid?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Varios fondos especializados compiten para ofrecerte condiciones más favorables, reduciendo el tiempo de tramitación y aumentando tu rentabilidad."
            }
          },
          {
            "@type": "Question",
            "name": "¿Cuál es el plazo usual de un préstamo flipping?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Generalmente de 6 a 18 meses, según la magnitud de la reforma y la estrategia de venta del inversor."
            }
          }
        ]
      }
    `;
  } else if (currentLang === 'en') {
    metaTitle = 'Flipping Loan - Buy, Renovate and Sell Fast | Finanbid';
    metaDescription =
      'Agile financing to purchase, renovate, and quickly sell properties. Specialized funds compete to offer you the best fix and flip loan.';
    metaKeywords =
      'flipping loan, fix and flip, real estate investment, private capital, short-term loan, buy-renovate-sell';
    ogTitle = metaTitle;
    ogDescription = metaDescription;

    faqJsonLd = `
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is property flipping?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "It involves purchasing a property at a good price, renovating it, and then selling it quickly for a profit."
            }
          },
          {
            "@type": "Question",
            "name": "Why get a Flipping Loan through Finanbid?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Multiple specialized funds compete to provide more favorable terms, speeding up approvals and maximizing your returns."
            }
          },
          {
            "@type": "Question",
            "name": "What is the usual term of a flipping loan?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Typically 6 to 18 months, depending on the scale of renovations and how quickly you plan to sell the property."
            }
          }
        ]
      }
    `;
  } else if (currentLang === 'pt') {
    metaTitle = 'Empréstimo Flipping - Compre, Renove e Venda Rápido | Finanbid';
    metaDescription =
      'Financiamento ágil para comprar, reformar e vender imóveis a curto prazo. Fundos especializados competem para oferecer o melhor empréstimo flipping.';
    metaKeywords =
      'empréstimo flipping, fix and flip, investimento imobiliário, capital privado, curto prazo';
    ogTitle = metaTitle;
    ogDescription = metaDescription;

    faqJsonLd = `
      { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [] }
    `;
  } else if (currentLang === 'sv') {
    metaTitle = 'Flipping Lån - Köp, Renovera och Sälj Snabbt | Finanbid';
    metaDescription =
      'Snabb finansiering för att köpa, renovera och snabbt sälja fastigheter. Specialiserade fonder tävlar för att erbjuda dig det bästa flipping-lånet.';
    metaKeywords =
      'flipping-lån, fix and flip, fastighetsinvestering, privat kapital, kortsiktig finansiering';
    ogTitle = metaTitle;
    ogDescription = metaDescription;

    faqJsonLd = `
      { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [] }
    `;
  } else if (currentLang === 'de') {
    metaTitle = 'Flipping-Darlehen - Kaufen, Renovieren und Schnell Verkaufen | Finanbid';
    metaDescription =
      'Schnelle Finanzierung für den Kauf, die Renovierung und den schnellen Verkauf von Immobilien. Spezialisierte Fonds konkurrieren, um Ihnen das beste Fix-and-Flip-Darlehen zu bieten.';
    metaKeywords =
      'flipping-darlehen, fix and flip, immobilieninvestition, privates kapital, kurzzeitdarlehen';
    ogTitle = metaTitle;
    ogDescription = metaDescription;

    faqJsonLd = `
      { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [] }
    `;
  } else if (currentLang === 'ar') {
    metaTitle = 'قرض الفليبنج - الشراء والتجديد والبيع السريع | Finanbid';
    metaDescription =
      'تمويل سريع لشراء وتجديد وبيع العقارات بسرعة. تتنافس صناديق متخصصة لتقديم أفضل قرض flipping.';
    metaKeywords =
      'قرض flipping, fix and flip, استثمار عقاري, رأس مال خاص, تمويل قصير الأجل';
    ogTitle = metaTitle;
    ogDescription = metaDescription;

    faqJsonLd = `
      { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [] }
    `;
  } else {
    // fallback: es
    metaTitle = 'Préstamo Flipping - Compra, Reforma y Venta Rápida | Finanbid';
    metaDescription =
      'Financiación ágil para comprar, reformar y vender inmuebles a corto plazo. Fondos especializados pujan para ofrecerte el mejor préstamo flipping.';
    metaKeywords =
      'préstamo flipping, fix and flip, inversión inmobiliaria, capital privado, corto plazo, compra-venta rápida';
    ogTitle = metaTitle;
    ogDescription = metaDescription;
    faqJsonLd = `
      { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [] }
    `;
  }

  const canonicalUrl = 'https://www.finanbid.com/prestamoflipping';
  return (
    <div className="bg-back text-white lg:mt-10 mt-24">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />

        <link rel="canonical" href={canonicalUrl} />

        {/* Hreflang alternates */}
        <link rel="alternate" href="https://www.finanbid.com/prestamoflipping" hreflang="x-default" />
        <link rel="alternate" href="https://es.finanbid.com/prestamoflipping" hreflang="es" />
        <link rel="alternate" href="https://en.finanbid.com/prestamoflipping" hreflang="en" />
        <link rel="alternate" href="https://pt.finanbid.com/prestamoflipping" hreflang="pt" />
        <link rel="alternate" href="https://sv.finanbid.com/prestamoflipping" hreflang="sv" />
        <link rel="alternate" href="https://de.finanbid.com/prestamoflipping" hreflang="de" />
        <link rel="alternate" href="https://ar.finanbid.com/prestamoflipping" hreflang="ar" />

        {/* Open Graph */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://www.finanbid.com/imagenenlace.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@finanbid" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://www.finanbid.com/imagenenlace.png" />

        {/* JSON-LD FAQ */}
        <script type="application/ld+json">
          {faqJsonLd}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-back dark:bg-gray-900">
        <div className="max-w-screen-xl px-4 pt-20 pb-8 mx-auto lg:gap-8 xl:gap-0 lg:py-16 lg:pt-28">
          <div className="place-self-center">
            <h1 className="max-w-2xl mb-4 text-4xl font-extrabold leading-none tracking-tight md:text-5xl xl:text-6xl dark:text-white">
              {t('prestamoFlipping.hero.title')}
            </h1>
            <p className="max-w-2xl mb-6 font-light text-secondary lg:mb-8 md:text-lg lg:text-xl dark:text-gray-400">
              {t('prestamoFlipping.hero.subtitle')}
            </p>
            <p className="max-w-2xl mb-6 font-light text-white lg:mb-8 md:text-lg lg:text-xl dark:text-gray-400">
              {t('prestamoFlipping.hero.description')}
            </p>
            <button
              onClick={() => navigate('/subir-proyecto')}
              className="bg-primary text-white font-bold py-3 px-6 rounded-lg hover:bg-gray-800"
            >
              {t('prestamoFlipping.hero.button')}
            </button>
          </div>
        </div>
      </section>

      {/* Principales Usos del Préstamo Flipping */}
      <section className="px-4 py-16 mx-auto sm:max-w-xl bg-gray-200 mb-12 md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8 lg:py-20 rounded-xl shadow-lg transform transition-transform hover:translate-y-[-10px]">
        <div className="max-w-xl mb-10 md:mx-auto sm:text-center lg:max-w-2xl md:mb-12">
          <div>
            <p className="inline-block px-3 py-px mb-4 text-xs font-semibold tracking-wider text-teal-900 uppercase rounded-full bg-sky-500 text-white">
              {t('prestamoFlipping.uses.tag')}
            </p>
          </div>
          <h2 className="max-w-lg mb-6 font-sans text-3xl font-bold leading-none tracking-tight text-back sm:text-4xl md:mx-auto">
            {t('prestamoFlipping.uses.title')}
          </h2>
          <p className="text-base text-secondary md:text-lg">
            {t('prestamoFlipping.uses.subtitle')}
          </p>
        </div>

        <div className="grid max-w-md gap-8 row-gap-10 sm:mx-auto lg:max-w-full lg:grid-cols-3">
          {/* Section for quick renovations */}
          <div className="flex flex-col sm:flex-row">
            <div className="sm:mr-4">
              <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-back">
                <svg className="w-12 h-12 text-green-400" stroke="currentColor" viewBox="0 0 52 52">
                  <polygon strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" points="29 13 14 29 25 29 23 39 38 23 27 23"></polygon>
                </svg>
              </div>
            </div>
            <div>
              <h6 className="mb-2 text-secondary font-semibold leading-5">
                {t('prestamoFlipping.uses.renovation.title')}
              </h6>
              <p className="mb-3 text-sm text-gray-900">
                {t('prestamoFlipping.uses.renovation.description1')}
              </p>
              <p className="mb-3 text-sm text-gray-900">
                {t('prestamoFlipping.uses.renovation.description2')}
              </p>
            </div>
          </div>
          {/* Section for purchase and resell */}
          <div className="flex flex-col sm:flex-row">
            <div className="sm:mr-4">
              <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-back">
                <svg className="w-12 h-12 text-green-400" stroke="currentColor" viewBox="0 0 52 52">
                  <polygon strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" points="29 13 14 29 25 29 23 39 38 23 27 23"></polygon>
                </svg>
              </div>
            </div>
            <div>
              <h6 className="mb-2 text-secondary font-semibold leading-5">
                {t('prestamoFlipping.uses.purchaseResell.title')}
              </h6>
              <p className="mb-3 text-sm text-gray-900">
                {t('prestamoFlipping.uses.purchaseResell.description1')}
              </p>
              <p className="mb-3 text-sm text-gray-900">
                {t('prestamoFlipping.uses.purchaseResell.description2')}
              </p>
            </div>
          </div>
          {/* Section for adding value */}
          <div className="flex flex-col sm:flex-row">
            <div className="sm:mr-4">
              <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-back">
                <svg className="w-12 h-12 text-green-400" stroke="currentColor" viewBox="0 0 52 52">
                  <polygon strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" points="29 13 14 29 25 29 23 39 38 23 27 23"></polygon>
                </svg>
              </div>
            </div>
            <div>
              <h6 className="mb-2 text-secondary font-semibold leading-5">
                {t('prestamoFlipping.uses.valueAddition.title')}
              </h6>
              <p className="mb-3 text-sm text-gray-900">
                {t('prestamoFlipping.uses.valueAddition.description1')}
              </p>
              <p className="mb-3 text-sm text-gray-900">
                {t('prestamoFlipping.uses.valueAddition.description2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Características del Préstamo Flipping */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-8 text-center">
            {t('prestamoFlipping.features.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {['feature1', 'feature2', 'feature3'].map((feature, index) => (
              <div key={index} className="p-6 bg-gray-800 rounded-lg text-center">
                <h3 className="text-xl font-semibold">
                  {t(`prestamoFlipping.features.${feature}.title`)}
                </h3>
                <p className="mt-4 text-gray-300">
                  {t(`prestamoFlipping.features.${feature}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action */}
      <section className="py-16 bg-back text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            {t('flipping.cta.title')}
          </h2>
          <p className="text-lg mb-8">
            {t('flipping.cta.description')}
          </p>
          <button
            onClick={() => navigate('/subir-proyecto')}
            className="bg-primary text-white font-bold py-3 px-6 rounded-lg hover:bg-gray-800"
          >
            {t('flipping.cta.button')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default PrestamoFlipping;
